import { useTranslation } from "react-i18next";

import {
  IOption,
  SelectDropdown,
} from "../../../../../components/SelectDropdown";
import { TextField } from "../../../../../components/TextField";
import {
  ErrorText,
  FieldRow,
  MutedText,
  Panel,
  PanelTitle,
} from "../../SurveyBuilder.styles";
import { generateCodeFromName, isValidSurveyCode } from "../../surveyCode";

export interface INewSurveyValues {
  name: string;
  description: string;
  type?: string;
  code: string;
  codeEdited: boolean;
}

export const emptyNewSurvey: INewSurveyValues = {
  name: "",
  description: "",
  code: "",
  codeEdited: false,
};

export function canCreateSurvey(values: INewSurveyValues) {
  return (
    values.name.trim() !== "" && !!values.type && isValidSurveyCode(values.code)
  );
}

interface INewSurveyFormProps {
  values: INewSurveyValues;
  surveyTypeOptions: IOption[];
  onChange(values: INewSurveyValues): void;
}

/** EN-5261: a new survey starts as draft version 1; questions are added once it exists. */
export function NewSurveyForm({
  values,
  surveyTypeOptions,
  onChange,
}: INewSurveyFormProps) {
  const { t } = useTranslation();

  return (
    <Panel>
      <PanelTitle>{t("surveys_builder_new_title")}</PanelTitle>
      <FieldRow>
        {t("surveys_builder_surveyInfo_surveyTitle")}
        <TextField
          label=""
          maxLength={200}
          value={values.name}
          backgroudColor="white"
          placeholder={t("surveys_builder_titlePlaceholder")}
          onChange={(e) =>
            onChange({
              ...values,
              name: e.target.value,
              code: values.codeEdited
                ? values.code
                : generateCodeFromName(e.target.value),
            })
          }
        />
      </FieldRow>
      <FieldRow>
        {t("surveys_builder_surveyInfo_surveyDescription")}
        <TextField
          label=""
          value={values.description}
          backgroudColor="white"
          placeholder={t("surveys_builder_descriptionPlaceholder")}
          onChange={(e) => onChange({ ...values, description: e.target.value })}
        />
      </FieldRow>
      <FieldRow as="div">
        {t("surveys_builder_content_type")}
        <SelectDropdown
          placeholder={t("surveys_builder_content_selectSurveyType")}
          options={surveyTypeOptions}
          value={values.type}
          width={300}
          onValueChange={(value) =>
            onChange({ ...values, type: value as string })
          }
        />
      </FieldRow>
      <FieldRow>
        {t("surveys_builder_surveyInfo_surveyCode")}
        <div>
          <TextField
            label=""
            value={values.code}
            backgroudColor="white"
            onChange={(e) =>
              onChange({ ...values, code: e.target.value, codeEdited: true })
            }
          />
          {!isValidSurveyCode(values.code) && (
            <ErrorText>{t("surveys_builder_surveyInfo_codeFormat")}</ErrorText>
          )}
        </div>
      </FieldRow>
      <MutedText>{t("surveys_builder_new_hint")}</MutedText>
    </Panel>
  );
}
